// This file is used to map the fields for the NGL Wisconsin Goods and Services Form
import { getNGLFieldNames } from '@/components/documents/Foxit/FormMapping/FieldNameProviders/NGLFieldNameProvider'
import { useCustomItemFields } from '@/components/documents/Foxit/FormMapping/composables/CustomItemFields'
import {
	MultipleItemsMethod,
	useFormFillHelper,
} from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useIdentitySetup } from '@/components/documents/Foxit/FormMapping/composables/IdentityFields'
import { useSupplementalFields } from '@/components/documents/Foxit/FormMapping/composables/SupplementalFields'
import {
	groupedTotal,
	useTotalFields,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import type {
	GroupedTotalConfig,
	SectionTotalConfig,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import type { CartItemAndLineItem } from '@/mixins/merchandise'
import type { CustomCartItem } from '@/types'

export default class NGLWIGoodsAndServicesForm {
	private fieldNameProvider = getNGLFieldNames()

	// Standard list of fields for this form.
	// These values point to field names in the fieldNameProvider, as well as functions to get the value for the field via the fieldValueProvider
	private fields: (keyof typeof this.fieldNameProvider)[] = [
		// Header
		'insuredFullName',
		'packageInfo',
		'dispositionCheckBoxFields',

		// Header / Identity
		'funeralHomeName',

		// Service
		'embalmingAndRefrigerationFields',
		'hairDresserFields',
		'cremationServicesFields',
		'visitationFields',
		'facilityUseOtherFields',
		'otherPrepFields',
		'utilityVehicleVanFields',
		'leadCarFields',
		'hearseFields', // Funeral Coach/Hearse
		'familyCarLimoFields',
		'transferMileRadiusFields',
		'acknowledgeCardsFields',
		'basicServicesFields',
		'funeralMemorialServiceFields',
		'gravesideServiceFields', // Committal Service/Final Placement
		'servicesOther1Fields',
		'servicesOther2Fields',
		'servicesOther3AndDiscountFields',

		// Merchandise
		'casketFields',
		'merchandiseOtherAndDiscountFields',
		'memorialFoldersFields',
		'memorialBookFields',
		'outerBurialFields',
		'urnFields',

		// Cash Advance
		'deathCertificatesFields',
		'intermentFields', // Grave Opening/Closing
		'obituaryNoticePublicationFields', // Newspaper Notice
		'flowersFields',
		'clergyHonorariumFields',
		'musicFields',
		'afterwardCateringFields', // Luncheon
		'coronerMedExaminerFields',
		'monumentMarkerAllowanceFields', // Engraving
		'transportFields', // Transportation
		'multilineCashAdvanceOtherFieldsWithDiscount', // Other Fields

		// Identity
		'purchaserPhone',
		'purchaserZip',
		'purchaserState',
		'purchaserCity',
		'purchaserAddress',
		'funeralHomePhone',
		'funeralHomeZip',
		'funeralHomeCity',
		'funeralHomeState',
		'funeralHomeStreet',
	]

	private unfilledFields = [
		'contractNumber',
		'gEmbalmingViewingCheck',
		'gEmbalmingViewingCheck',
		'gEmbalmingPersonalCheck',
		'gEmbalmingOtherCheck',
		'reasonForEmbalmingDesc1',
		'gOuterBurialCemeteryRequiredCheck',
		'gCrematoryAltContainerRequiredCheck',
	]
	private sectionTotals: SectionTotalConfig[] = [
		{ section: 'service' },
		{ section: 'merchandise' },
		{ section: 'cashAdvance' },
	]
	private groupedTotals: GroupedTotalConfig[] = [
		groupedTotal('guaranteed'),
		groupedTotal('funeral'),
	]

	// Setup prior to building form filling array.
	constructor(planHolderId: string, cartItems?: CartItemAndLineItem[]) {
		const { identitySetup } = useIdentitySetup()
		const {
			setMultipleItemsMethod,
			setFieldNameProvider,
			setCartItems,
			setIgnoreSupplementalTextList,
			setNonServiceCategories,
			setOtherLineCounts,
		} = useFormFillHelper()

		// The WI G&S form has six 'Other' cash advance lines (ngOtherCashAdvanceLine1-6)
		setOtherLineCounts({
			cashAdvance: 6,
		})
		identitySetup(planHolderId, ['planHolder', 'funeralHome'])
		setIgnoreSupplementalTextList(
			new Set([
				'gCasketModelDesc',
				'gCasketExteriorDesc',
				'gCasketInteriorDesc',
				'gOuterBurialModelDesc',
				'gOuterBurialConstructedOfDesc',
				'gUrnModelDesc',
				'gUrnConstructedOfDesc',
			])
		)

		setMultipleItemsMethod(MultipleItemsMethod.Supplemental)
		setFieldNameProvider(this.fieldNameProvider)
		setNonServiceCategories()

		if (cartItems) {
			setCartItems(cartItems)
		}
	}

	// Build form filling array.
	getFormMappings() {
		const { formatFields, mapFields, setFieldNameProvider } =
			useFormFillHelper()
		const { getTotalsFields } = useTotalFields()
		const { getSupplementalFields } = useSupplementalFields()

		setFieldNameProvider(this.fieldNameProvider)
		const mainFields = mapFields(this.fields)
		const supplementalFields = getSupplementalFields(mainFields)
		const totalFields = getTotalsFields(
			mainFields,
			this.sectionTotals,
			this.groupedTotals
		)

		return formatFields([...mainFields, ...supplementalFields, ...totalFields])
	}

	// Mappings for only the fields a custom item is filled into, so adding one
	// doesn't overwrite what the AFP entered by hand
	getCustomItemFormMappings(customItemId: CustomCartItem['id']) {
		const { getCustomItemFormMappings } = useCustomItemFields()

		return getCustomItemFormMappings(
			this.getFormMappings(),
			this.fields,
			customItemId
		)
	}
}
